<script setup>
/**
 * Notion.
 *
 * Same single-card page as Linear, except connecting goes through Notion's
 * OAuth consent screen rather than a plain link, so the card's `action` slot
 * carries a button that asks the server for the authorization URL first.
 */
import { ref, computed, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import ButtonNext from 'next/button/Button.vue';
import notionClient from 'dashboard/api/notion_auth.js';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Integration from './Integration.vue';

const { t } = useI18n();
const store = useStore();
const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'notion');

const uiFlags = useMapGetter('integrations/getUIFlags');

const isLoading = computed(
  () => !integrationLoaded.value || uiFlags.value.isCreatingNotion
);

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }

  return '';
});

const authorize = async () => {
  const response = await notionClient.generateAuthorization();
  const {
    data: { url },
  } = response;

  window.location.href = url;
};

const initializeNotionIntegration = async () => {
  await store.dispatch('integrations/get', 'notion');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeNotionIntegration();
});
</script>

<template>
  <SettingsPageShell :title="$t('INTEGRATION_SETTINGS.NOTION.HEADER')">
    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.LOADING')"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <Integration
      v-else
      :integration-id="integration.id"
      :integration-name="integration.name"
      :integration-description="integration.description"
      :integration-enabled="integration.enabled"
      :integration-action="integrationAction"
      :delete-confirmation-text="{
        title: t('INTEGRATION_SETTINGS.NOTION.DELETE.TITLE'),
        message: t('INTEGRATION_SETTINGS.NOTION.DELETE.MESSAGE'),
      }"
    >
      <template #action>
        <ButtonNext
          size="sm"
          variant="faded"
          color="blue"
          :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
          @click="authorize"
        />
      </template>
    </Integration>
  </SettingsPageShell>
</template>
